import { MouseEvent, useRef, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import BalanceIcon from "@mui/icons-material/Balance";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  useTheme,
} from "@mui/material";

import { CRPS_ORANGE_DARK, CRPS_ORANGE_LIGHT, Grade, PSTooltip } from "../../utils";

export const PowerSortButtonGroup = (props: {
  disabled: boolean;
  grades: Grade[];
  role: string;
  sort: (grades: string[]) => Promise<void>;
}) => {
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [sortingGrade, setSortingGrade] = useState<Grade | null>(null);

  const handleClickAll = (event: MouseEvent) => {
    setSortingGrade(null);
    setOpen(false);
    setSortDialogOpen(true);
  };

  const handleClickOne = (event: MouseEvent, grade: Grade) => {
    setSortingGrade(grade);
    setOpen(false);
    setSortDialogOpen(true);
  };

  const handleClosePopper = (event: Event) => {
    setSortingGrade(null);
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  return !props.role.includes("ADMIN") ? (
    <PSTooltip
      child={
        <span>
          <Button disabled startIcon={<BalanceIcon />} sx={{ pl: 2, pr: 7.5 }} variant="contained">
            PowerSort
          </Button>
        </span>
      }
      title={"Only available to Admins"}
    ></PSTooltip>
  ) : props.grades.length < 1 ? (
    <PSTooltip
      child={
        <span>
          <Button disabled startIcon={<BalanceIcon />} sx={{ pl: 2, pr: 7.5 }} variant="contained">
            PowerSort
          </Button>
        </span>
      }
      title={"No grades available to PowerSort"}
    ></PSTooltip>
  ) : (
    <>
      <ButtonGroup
        disabled={props.disabled}
        ref={anchorRef}
        sx={{
          borderColor: "inherit",
          "& .MuiButtonGroup-grouped": {
            borderColor: "inherit",
          },
        }}
        variant="contained"
      >
        <Button
          onClick={handleClickAll}
          startIcon={<BalanceIcon />}
          sx={{ bgcolor: theme.palette.mode === "light" ? CRPS_ORANGE_LIGHT : CRPS_ORANGE_DARK }}
        >
          PowerSort
        </Button>
        <Button
          onClick={ev => setOpen(o => !o)}
          size="small"
          sx={{ bgcolor: theme.palette.mode === "light" ? CRPS_ORANGE_LIGHT : CRPS_ORANGE_DARK }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper anchorEl={anchorRef.current} disablePortal open={open} role={undefined} sx={{ zIndex: 1 }} transition>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClosePopper}>
                <MenuList id="powersort-menu" autoFocusItem>
                  {props.grades.map(gr => (
                    <MenuItem disabled={!props.grades.includes(gr)} key={gr} onClick={ev => handleClickOne(ev, gr)}>
                      {`${gr} only`}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      <Dialog open={sortDialogOpen}>
        <DialogTitle>
          PowerSort {sortingGrade ? `${sortingGrade.replace("Gr", "Grade")} Only` : "All Grades"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Proceeding will discard current sorted classes. Continue to re-run PowerSort
            {sortingGrade ? ` for ${sortingGrade} only` : " across all grades"}?{<br />}
            {<br />}
            {sortingGrade ? (
              <span style={{ fontStyle: "italic" }}>
                Please note that split-grade classrooms can still be impacted.
              </span>
            ) : (
              ""
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={ev => {
              setSortingGrade(null);
              setSortDialogOpen(false);
            }}
          >
            No, cancel
          </Button>
          <Button
            onClick={ev => {
              setSortDialogOpen(false);
              props.sort(sortingGrade ? [sortingGrade] : props.grades);
            }}
            variant="contained"
          >
            Yes, re-PowerSort
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
